import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Game } from '@app/shared/models/game.model';
import { ShopOffersInventory } from '@app/shared/models/inventory.model';
import { ShopOffer } from '@app/shared/models/offer.model';
import { LimitedTimeEvent, LimitedTimeEventType } from '@app/shared/models/limitedTimeEvent.model';
import { ModalConfig, Modal, ModalType } from '@app/shared/models/modal.model';
import { FakePurchaseRate } from '@app/shared/models/testerModal.model';
import { User } from '@app/shared/models/user.model';
import { GoldenBombService } from '@app/shared/services/goldenBomb.service';
import { TagsService } from '@app/shared/services/tags.service';
import { UserService } from '@app/shared/services/user.service';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { GamesRepository } from '@app/shared/state/games/games.repository';
import { GoldenBombRepository } from '@app/shared/state/golden-bomb/golden-bomb.repository';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { WalletRepository } from '@app/shared/state/wallet/wallet.repository';
import { EventManager } from '@app/shared/utils/event-manager';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';
import { OffersRepository } from '@app/shared/state/offer/offer.repository';

export type GameType = 'Casino' | 'Bar';

@Injectable({ providedIn: 'root' })
export class TesterModalFacade {
  private readonly eventManager = inject(EventManager);
  private readonly gamesRepo = inject(GamesRepository);
  private readonly walletRepo = inject(WalletRepository);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly shopRepo = inject(ShopRatesRepository);
  private readonly offersRepo = inject(OffersRepository);
  private readonly goldenBombService = inject(GoldenBombService);
  private readonly goldenBombRepo = inject(GoldenBombRepository);
  private readonly userService = inject(UserService);
  private readonly userRepo = inject(UserRepository);
  private readonly tagsService = inject(TagsService);

  testerModalData: ModalConfig;

  readonly shopWithPrices = toSignal(
    this.userRepo.user$.pipe(
      filter((user) => !!user?.id),
      take(1),
      map(() => this.shopRepo.getShopRates()),
      catchError(() => of({ Coins: [], Gems: [] } as ShopOffersInventory))
    ),
    { initialValue: { Coins: [], Gems: [] } as ShopOffersInventory }
  );

  readonly shopOffers = toSignal(
    this.userRepo.user$.pipe(
      filter((user) => !!user?.id),
      take(1),
      switchMap(() => this.offersRepo.offers$),
      catchError(() => of([] as ShopOffer[]))
    ),
    { initialValue: [] as ShopOffer[] }
  );

  readonly coinsShopRatesFaked = computed<FakePurchaseRate[]>(() =>
    this.shopWithPrices().Coins.map((coin) => ({
      id: crypto.randomUUID(),
      sku: coin.sku,
      type: 'Shop',
      productType: 'Coins',
      amount: coin.value,
      priceEur: coin.amount,
    }))
  );

  readonly gemsShopRatesFaked = computed<FakePurchaseRate[]>(() =>
    this.shopWithPrices().Gems.map((gem) => ({
      id: crypto.randomUUID(),
      sku: gem.sku,
      type: 'Shop',
      productType: 'Gems',
      amount: gem.value,
      priceEur: gem.amount,
    }))
  );

  readonly offersShopRatesFaked = computed<FakePurchaseRate[]>(() =>
    this.shopOffers().map((offer) => ({
      id: crypto.randomUUID(),
      sku: offer.sku,
      type: 'Offer',
      productType: offer.currency,
      amount: offer.value,
      priceEur: offer.finalPrice.amount.toString(),
    }))
  );

  readonly allRatesFaked = computed<FakePurchaseRate[]>(() => [
    ...this.coinsShopRatesFaked(),
    ...this.gemsShopRatesFaked(),
    ...this.offersShopRatesFaked(),
  ]);

  loadGoldenBomb(): void {
    this.goldenBombService
      .get()
      .pipe(take(1))
      .subscribe((goldenBomb) => {
        if (goldenBomb.isActive) {
          goldenBomb.canRedeem = true;
          this.goldenBombRepo.updateGoldenBomb(goldenBomb);
          this.goldenBombService.createInterval(goldenBomb);
        }
      });
  }

  init(data: ModalConfig) {
    this.testerModalData = data;
  }

  reloadQueryParams(selectedVersion: string): void {
    location.href = `https://${location.host}?FrontendVersion=${selectedVersion}`;
  }

  reloadPage() {
    window.location.reload();
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(this.testerModalData.id, ModalType.View, this.testerModalData));
  }

  user(): User {
    return this.userRepo.getUser();
  }

  async updateUser() {
    await this.userService.fetchCurrentUser();
  }

  games(): Game[] {
    return this.gamesRepo.getAllGames();
  }

  onSubmitVersion(selectedVersion: string): void {
    this.reloadQueryParams(selectedVersion);
  }

  getSortedGames(): Game[] {
    return this.games().sort((a, b) => a.gameTitle.localeCompare(b.gameTitle));
  }

  copyWallet(creditType: string) {
    if (creditType === 'Credits') {
      navigator.clipboard.writeText(this.walletRepo.getWallet().totalCredits.toString());
    } else if (creditType === 'Gems') {
      navigator.clipboard.writeText(this.walletRepo.getWallet().totalGems.toString());
    }
  }

  getChallengeEvents(): LimitedTimeEvent[] {
    return this.eventsRepo
      .getAllEvents()
      .filter(
        (event) =>
          event.type === LimitedTimeEventType.DailyChallenge || event.type === LimitedTimeEventType.GameChallenges
      );
  }

  manageNewUnlockedTag() {
    this.tagsService.processNewUnlockedTag();
  }

  manageTestMachines(value: boolean): void {
    const testTag = this.tagsService.getTestTag();
    const testerGames = this.gamesRepo
      .getAllGames()
      .filter((game) => game.tags.some((tag) => tag.name === testTag.name));

    this.gamesRepo.upsertManyGames(testerGames.map((game) => ({ ...game, isHidden: !value })));
  }
}
